import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import '@/assets/css/datepicker.css';
import '@/assets/css/common.css';
import '@/assets/css/chart.css';

import {
  ALL_TYPES,
  CONTRACT_ADDABLE_TYPES,
  DEALER_TYPE,
  PARTNER_TYPES,
  SETTLEABLE_TYPES
} from '@/constants/bizTargetType';
import { STATS_REPORT_BIZ_TYPES } from '@/constants/bizType';
import { CONTRACT_MANAGABLE_ROLES, PARTNER_MANAGABLE_ROLES, SETTLEABLE_ROLES } from '@/constants/role';
import { ROUTES } from '@/constants/routes';
import { createPrivateRoutes } from '@/lib/react-router-dom/createPrivateRoutes';
import ContainerOutlet from '@/outlets/ContainerOutlet';
import Root from '@/pages/root';
import {
  preLoadAddContract,
  preLoadAddSvc,
  preLoadBusiness,
  preLoadDashboardInfo,
  preLoadDealerContract,
  preLoadEditSvc,
  preLoadMembers,
  preLoadPartner,
  preLoadPartnerContractDetail,
  preLoadPartnerContractForDealer,
  preLoadPartnerContractForPartner,
  preLoadPartnerDetail,
  preLoadPartnerList,
  preLoadSettleBaseInfo,
  preLoadSettleInvoice,
  preLoadSettleInvoiceDetail,
  preLoadSettleTaxInvoice,
  preLoadStatsReport,
  preLoadSvcDetail,
  preLoadSvcList,
  preLoadUsage
} from '@/utils/preLoaders';

// createPrivateRoutes를 사용하지 않는 route에서 Suspense가 필요
const lazyWithSuspense = (importFn: () => Promise<{ default: React.ComponentType }>) => {
  const LazyComponent = lazy(importFn);
  return (
    <Suspense fallback={<></>}>
      <LazyComponent />
    </Suspense>
  );
};

/** 비동기 컴포넌트 */
// 에러 페이지
const ErrorElement = lazy(() => import('@/components/ErrorElement'));
const InternalError = lazyWithSuspense(() => import('@/pages/error/internal'));
const MaintenanceError = lazyWithSuspense(() => import('@/pages/error/maintenance'));
const NotPartner = lazyWithSuspense(() => import('@/pages/error/not-partner'));
const NotFound = lazyWithSuspense(() => import('@/pages/not-found'));
const ExpiredInvitation = lazyWithSuspense(() => import('@/pages/expired-invitation'));

// 내정보
const Myinfo = lazyWithSuspense(() => import('@/pages/account/myinfo'));
const Leave = lazyWithSuspense(() => import('@/pages/account/leave'));

// 약관 페이지
const Terms = lazyWithSuspense(() => import('@/pages/terms'));

// 이용기관
const PartnerList = lazy(() => import('@/pages/:AUTH_INFO_ID/partner'));
const PartnerDetail = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/:PARTNER_ID'));
const PartnerSvcList = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/:PARTNER_ID/svc'));
const PartnerSvcDetail = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/:PARTNER_ID/svc/:SVC_ID'));
const PartnerRegister = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/register'));
const AddPartnerSvc = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/:PARTNER_ID/svc/add'));
const EditPartnerSvc = lazy(() => import('@/pages/:AUTH_INFO_ID/partner/:PARTNER_ID/svc/:SVC_ID/edit'));
const RegisterApp = lazy(() => import('@/pages/:AUTH_INFO_ID/app/register'));

// 정산관리
const Invoice = lazy(() => import('@/pages/:AUTH_INFO_ID/settle/invoice'));
const SettleInvoiceDetail = lazy(() => import('@/pages/:AUTH_INFO_ID/settle/invoice/:SETTLE_INVOICE_ID'));
const TaxInvoiceIssuance = lazy(() => import('@/pages/:AUTH_INFO_ID/settle/taxInvoiceIssuance'));
const BaseInfo = lazy(() => import('@/pages/:AUTH_INFO_ID/settle/baseInfo'));

// 계약관리
const AddContract = lazy(() => import('@/pages/:AUTH_INFO_ID/contract/add'));
const DealerContract = lazy(() => import('@/pages/:AUTH_INFO_ID/contract/dealer'));
const PartnerContractForDealer = lazy(() => import('@/pages/:AUTH_INFO_ID/contract/partner/for-dealer'));
const PartnerContractForPartner = lazy(() => import('@/pages/:AUTH_INFO_ID/contract/partner/for-partner'));
const PartnerContractDetail = lazy(() => import('@/pages/:AUTH_INFO_ID/contract/partner/:PARTNER_ID'));

// 정보관리
const DashBoard = lazy(() => import('@/pages/:AUTH_INFO_ID/dashboard'));
const Members = lazy(() => import('@/pages/:AUTH_INFO_ID/info/members'));
const Business = lazy(() => import('@/pages/:AUTH_INFO_ID/info/business'));

// 통계
const Usage = lazy(() => import('@/pages/:AUTH_INFO_ID/stats/usage'));
const Report = lazy(() => import('@/pages/:AUTH_INFO_ID/stats/report'));

export const router = createBrowserRouter([
  {
    errorElement: <ErrorElement />,
    children: [
      {
        element: <ContainerOutlet />,
        children: [
          /* 마이비즈 */
          { path: ROUTES.ROOT, element: <Root /> },
          /* 내정보 */
          { path: ROUTES.ACCOUNT.INDEX, element: Myinfo },
          { path: ROUTES.ACCOUNT.LEAVE, element: Leave },
          /* 에러페이지 */
          { path: ROUTES.ERROR.INTERNAL, element: InternalError },
          { path: ROUTES.ERROR.MAINTENANCE, element: MaintenanceError },
          { path: ROUTES.ERROR.EXPIRED_INVITAION, element: ExpiredInvitation },
          { path: ROUTES.ERROR.NOT_PARTNER, element: NotPartner },

          ...createPrivateRoutes({
            allowedTypes: ALL_TYPES,
            routes: [
              /* 대시보드 */
              { path: ROUTES.DASHBOARD, element: <DashBoard />, preLoader: preLoadDashboardInfo },
              /* 정보관리 > 내정보 */
              { path: ROUTES.INFO.MEMBERS, element: <Members />, preLoader: preLoadMembers },
              /* 정보관리 > 사업자 정보 */
              { path: ROUTES.INFO.BUSINESS, element: <Business />, preLoader: preLoadBusiness },
              /* 통계 > 통계 리포트 */
              {
                path: ROUTES.STATS.REPORT,
                element: <Report />,
                preLoader: preLoadStatsReport,
                allowedBizTypes: STATS_REPORT_BIZ_TYPES
              },
              /* 통계 > 이용 내역 조회 */
              { path: ROUTES.STATS.USAGE, element: <Usage />, preLoader: preLoadUsage }
            ]
          }),

          /* 이용기관(딜러사 전용) */
          ...createPrivateRoutes({
            allowedTypes: DEALER_TYPE,
            allowedRoles: PARTNER_MANAGABLE_ROLES,
            routes: [
              /* 이용기관 목록 */
              { path: ROUTES.PARTNER.INDEX, element: <PartnerList />, preLoader: preLoadPartnerList },
              /* 이용기관 조회 */
              { path: ROUTES.PARTNER.DETAIL, element: <PartnerDetail />, preLoader: preLoadPartnerDetail },
              /* 이용기관 등록 */
              { path: ROUTES.PARTNER.REGISTER, element: <PartnerRegister /> },
              /* 기관 추가 */
              { path: ROUTES.PARTNER.ADD_SVC, element: <AddPartnerSvc />, preLoader: preLoadAddSvc }
            ]
          }),

          /* 이용기관(파트너 전용) */
          ...createPrivateRoutes({
            allowedTypes: PARTNER_TYPES,
            allowedRoles: PARTNER_MANAGABLE_ROLES,
            routes: [
              /* 기관 목록 */
              { path: ROUTES.PARTNER.SVC, element: <PartnerSvcList />, preLoader: preLoadSvcList },
              /* 앱 정보 등록 */
              { path: ROUTES.APP.REGISTER, element: <RegisterApp />, preLoader: preLoadPartner }
            ]
          }),

          /* 이용기관(공통) */
          ...createPrivateRoutes({
            allowedTypes: ALL_TYPES,
            allowedRoles: PARTNER_MANAGABLE_ROLES,
            routes: [
              /* 기관 정보 조회 */
              { path: ROUTES.PARTNER.SVC_DETAIL, element: <PartnerSvcDetail />, preLoader: preLoadSvcDetail },
              /* 기관 정보 수정 */
              { path: ROUTES.PARTNER.EDIT_SVC, element: <EditPartnerSvc />, preLoader: preLoadEditSvc }
            ]
          }),

          /** 계약관리(딜러사 전용) */
          ...createPrivateRoutes({
            allowedTypes: DEALER_TYPE,
            allowedRoles: CONTRACT_MANAGABLE_ROLES,
            routes: [
              /* 딜러 계약 조회 */
              { path: ROUTES.CONTRACT.DEALER, element: <DealerContract />, preLoader: preLoadDealerContract },
              /* 이용기관 계약 조회 */
              {
                path: ROUTES.CONTRACT.PARTNER_FOR_DEALER,
                element: <PartnerContractForDealer />,
                preLoader: preLoadPartnerContractForDealer
              }
            ]
          }),

          /** 계약관리(파트너 전용) */
          ...createPrivateRoutes({
            allowedTypes: PARTNER_TYPES,
            allowedRoles: CONTRACT_MANAGABLE_ROLES,
            routes: [
              /* 계약조회 */
              {
                path: ROUTES.CONTRACT.PARTNER_FOR_PARTNER,
                element: <PartnerContractForPartner />,
                preLoader: preLoadPartnerContractForPartner
              }
            ]
          }),

          /** 계약관리(계약 추가) */
          ...createPrivateRoutes({
            allowedTypes: CONTRACT_ADDABLE_TYPES,
            allowedRoles: CONTRACT_MANAGABLE_ROLES,
            routes: [
              /* (딜러사) 이용기관 계약 추갸, (직계약) 계약 추가 */
              {
                path: ROUTES.CONTRACT.ADD,
                element: <AddContract />,
                preLoader: preLoadAddContract
              }
            ]
          }),

          /** 계약관리(공통) */
          ...createPrivateRoutes({
            allowedTypes: ALL_TYPES,
            allowedRoles: CONTRACT_MANAGABLE_ROLES,
            routes: [
              /* 계약 정보 상세 조회 */
              {
                path: ROUTES.CONTRACT.PARTNER_DETAIL,
                element: <PartnerContractDetail />,
                preLoader: preLoadPartnerContractDetail
              }
            ]
          }),

          /* 정산관리 */
          ...createPrivateRoutes({
            allowedTypes: SETTLEABLE_TYPES,
            allowedRoles: SETTLEABLE_ROLES,
            routes: [
              /* 정산내역 */
              { path: ROUTES.SETTLE.INVOICE, element: <Invoice />, preLoader: preLoadSettleInvoice },
              /* 정산내역 > 상세 청구 정보 */
              {
                path: ROUTES.SETTLE.INVOICE_DETAIL,
                element: <SettleInvoiceDetail />,
                preLoader: preLoadSettleInvoiceDetail
              },
              /* 세금계산서 발행 내역 */
              {
                path: ROUTES.SETTLE.TAX_INVOICE_ISSUANCE,
                element: <TaxInvoiceIssuance />,
                preLoader: preLoadSettleTaxInvoice
              },
              /* 정산 기본 정보 */
              { path: ROUTES.SETTLE.BASE_INFO, element: <BaseInfo />, preLoader: preLoadSettleBaseInfo }
            ]
          }),
          { path: '*', element: NotFound }
        ]
      },
      { path: ROUTES.TERMS, element: Terms }
    ]
  }
]);

const App = () => {
  return <RouterProvider router={router} />;
};

export default App;
